import { useEffect, useState, useCallback } from "react";
import { Camera, Trash2, Plus, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

function useAuthedBlob(url) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let localUrl = null;
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!alive) return;
        localUrl = URL.createObjectURL(blob);
        setSrc(localUrl);
      } catch (e) {}
    })();
    return () => {
      alive = false;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [url]);
  return src;
}

function PhotoTile({ matchId, photo, isCoach, onDelete, onOpen }) {
  const src = useAuthedBlob(`${API}/matches/${matchId}/photos/${photo.id}`);
  return (
    <div
      data-testid={`match-photo-tile-${photo.id}`}
      className="group relative aspect-square rounded-lg overflow-hidden bg-slate-800 border border-white/5"
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`match-photo-open-${photo.id}`}
        className="absolute inset-0 w-full h-full"
        aria-label="Ouvrir en grand"
      >
        {src ? (
          <img
            src={src}
            alt="Souvenir"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Camera className="w-8 h-8 text-slate-700" />
          </div>
        )}
      </button>
      {isCoach && (
        <button
          data-testid={`match-photo-delete-${photo.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(photo.id);
          }}
          title="Supprimer"
          className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-slate-900/85 hover:bg-rose-500/40 text-slate-200 hover:text-rose-100 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function Lightbox({ matchId, photos, index, onClose, onPrev, onNext }) {
  const photo = photos[index];
  const src = useAuthedBlob(photo ? `${API}/matches/${matchId}/photos/${photo.id}` : "");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  // Simple swipe navigation for touch devices
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (dx > 60) onPrev();
    else if (dx < -60) onNext();
    setTouchStart(null);
  };

  if (!photo) return null;
  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-sm flex flex-col"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="text-xs font-mono-stat uppercase tracking-widest text-slate-400">
          {index + 1} / {photos.length}
        </div>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-2 sm:px-8 pb-4">
        {src ? (
          <img
            src={src}
            alt="Souvenir agrandi"
            data-testid="lightbox-image"
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            draggable="false"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="text-slate-500 text-sm font-mono-stat uppercase">Chargement...</div>
        )}
        {photos.length > 1 && (
          <>
            <button
              data-testid="lightbox-prev"
              onClick={onPrev}
              aria-label="Photo précédente"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={onNext}
              aria-label="Photo suivante"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function MatchPhoto({ match, isCoach, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/matches/${match.id}/photos`);
      setPhotos(data);
    } catch (e) {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [match.id, match.photo_count]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    let failed = 0;
    for (const file of files) {
      if (file.size > 6 * 1024 * 1024) {
        toast.error(`${file.name} trop volumineux (max 6 Mo)`);
        failed++;
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await fetch(`${API}/matches/${match.id}/photo`, {
          method: "POST",
          credentials: "include",
          body: form,
        });
        if (!res.ok) throw new Error("upload failed");
        uploaded++;
      } catch (err) {
        failed++;
      }
    }
    setUploading(false);
    e.target.value = "";
    if (uploaded) toast.success(`${uploaded} photo(s) ajoutée(s)`);
    if (failed) toast.error(`${failed} échec(s)`);
    onChange && onChange();
    load();
  };

  const removeOne = async (photoId) => {
    if (!confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/matches/${match.id}/photos/${photoId}`);
      toast.success("Photo supprimée");
      onChange && onChange();
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const prev = useCallback(() => {
    setOpenIndex((i) => (i == null ? null : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const next = useCallback(() => {
    setOpenIndex((i) => (i == null ? null : (i + 1) % photos.length));
  }, [photos.length]);

  return (
    <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden no-print">
      <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#E5B85C]" />
          <div className="font-display font-bold text-sm uppercase tracking-wide">
            Album du match
          </div>
          {!loading && photos.length > 0 && (
            <span className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-500">
              · {photos.length} photo(s)
            </span>
          )}
        </div>
        {isCoach && (
          <label
            data-testid="upload-photo-button"
            className={`cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide ${
              uploading ? "bg-slate-700 text-slate-400" : "bg-[#E5B85C] text-[#0B0F17] hover:bg-[#D4A24E]"
            }`}
          >
            <input
              data-testid="photo-file-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={upload}
              disabled={uploading}
            />
            <Plus className="w-3.5 h-3.5" />
            {uploading ? "Upload..." : "Ajouter"}
          </label>
        )}
      </div>

      <div className="p-3">
        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Chargement...</div>
        ) : photos.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            {isCoach
              ? "Aucune photo pour l'instant. Ajoute des souvenirs du match."
              : "Le coach n'a pas encore uploadé de photos"}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {photos.map((p, i) => (
              <PhotoTile
                key={p.id}
                matchId={match.id}
                photo={p}
                isCoach={isCoach}
                onDelete={removeOne}
                onOpen={() => setOpenIndex(i)}
              />
            ))}
          </div>
        )}
      </div>

      {openIndex !== null && photos[openIndex] && (
        <Lightbox
          matchId={match.id}
          photos={photos}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
}
